/**
 * ActivityFeedPage — Design-Final-Fix (activities.html)
 *
 * Route-Komponente für /activities.
 * Prototyp-Struktur: Titel + Live-Pill + Export CSV, direkt gefolgt von
 * Kategorie-Chips und der Liste — keine KPI-Karten, keine Such-/Zeitraum-
 * Toolbar, keine Ereignistyp-/Hotspot-Dropdowns (activities.html hat davon
 * nichts; siehe Abnahme-Befund 2026-09-01).
 *
 * Kategorie-Chips (All/Rewards/Transfers/Legacy) mappen auf kinds-Sets →
 * action_type-Param an BE. "Legacy" hat keinen eigenen BE-kind, sondern
 * filtert clientseitig auf era='pre' (echtes Feld der ActivityGroup-API).
 * "Commissions" aus dem Prototyp-Mock entfaellt bewusst: das Backend schreibt
 * keine Commission-Aktivitaeten (kein Kind dafuer in api/v2/activities) —
 * ein Chip ohne jemals moegliche Treffer waere Kosmetik ohne Substanz.
 */
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActivityFeed } from './hooks/useActivityFeed'
import { useActivityStream } from './hooks/useActivityStream'
import { ActivityList } from './ActivityList'
import { useAccount } from '../settings/useAccount'
import { ActivityStreamIndicator } from './ActivityStreamIndicator'
import { Download } from 'lucide-react'
import { CATEGORY_KINDS } from './constants/actionTypeConfig'
import { buildCsvContent, triggerCsvDownload } from './activityUtils'

// --- Filter-Kategorien (Prototyp: All/Rewards/Transfers/Legacy) ---

type FilterCategory = 'all' | 'rewards' | 'transfers' | 'legacy'

/** Rewards/Transfers filtern serverseitig ueber action_type; Legacy hat kein
 *  eigenes BE-kind und wird unten clientseitig ueber era='pre' gefiltert. */
function categoryToServerActionType(category: FilterCategory): string | null {
  if (category === 'rewards') return CATEGORY_KINDS.rewards.join(',')
  if (category === 'transfers') return 'transfer'
  return null
}

// --- Section Header ---

type SectionHeaderProps = {
  onExportCsv: () => void
  streamConnected: boolean
}

function SectionHeader({ onExportCsv, streamConnected }: SectionHeaderProps) {
  const { t } = useTranslation('activity')
  return (
    <div className="section-header">
      <div>
        <h1>{t('page.title')}</h1>
        <p className="muted">
          {t('page.subtitle')} <ActivityStreamIndicator connected={streamConnected} />
        </p>
      </div>
      <button className="btn btn-secondary btn-sm" onClick={onExportCsv}>
        <Download size={14} />
        {t('header.exportCsv')}
      </button>
    </div>
  )
}

// --- Filter Chips ---

type FilterChipsProps = {
  filterCategory: FilterCategory
  onFilterCategoryChange: (cat: FilterCategory) => void
}

function FilterChips({ filterCategory, onFilterCategoryChange }: FilterChipsProps) {
  const { t } = useTranslation('activity')
  const chips: { key: FilterCategory; labelKey: string }[] = [
    { key: 'all', labelKey: 'filters.all' },
    { key: 'rewards', labelKey: 'filterTabs.rewards' },
    { key: 'transfers', labelKey: 'filterTabs.transfers' },
    { key: 'legacy', labelKey: 'filterTabs.legacy' },
  ]

  return (
    <div className="row" role="group" aria-label={t('filters.categoryLabel')} style={{ gap: '8px', marginBottom: '14px' }}>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className={`chip ${filterCategory === chip.key ? 'is-active' : ''}`}
          onClick={() => onFilterCategoryChange(chip.key)}
          data-testid={`activity-filter-${chip.key}`}
        >
          {t(chip.labelKey)}
        </button>
      ))}
    </div>
  )
}

// --- 404-Fallback ---

function ActivityComingSoon() {
  const { t } = useTranslation('activity')
  return (
    <div className="activity-coming-soon" role="status">
      <p>{t('comingSoon.message')}</p>
      <p className="text-muted">
        {t('comingSoon.details')}
      </p>
    </div>
  )
}

// --- Error-State ---

function ActivityError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('activity')
  return (
    <div className="error-state" role="alert">
      <p>{t('error.loadFailed')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Main Component ---

export function ActivityFeedPage() {
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all')

  // Ref zum Verhindern doppelter Download-Auslösung in StrictMode
  const downloadInProgress = useRef(false)

  const apiActionType = useMemo(() => categoryToServerActionType(filterCategory), [filterCategory])

  const { connected } = useActivityStream()
  const { account } = useAccount()
  const useMetric = account?.use_metric ?? true

  const { items, hasMore, loading, error, is404, dayLimit, loadMore, refetch } =
    useActivityFeed({ action_type: apiActionType })

  // "Legacy" hat kein eigenes BE-kind — clientseitiger Filter auf era='pre'
  // (echtes API-Feld, siehe activityApi.ts ActivityGroup.era).
  const filteredItems = useMemo(() => {
    if (filterCategory !== 'legacy') return items
    return items.filter((item) => item.era === 'pre')
  }, [items, filterCategory])

  const handleExportCsv = () => {
    if (downloadInProgress.current) return
    downloadInProgress.current = true
    const content = buildCsvContent(filteredItems)
    const datumStr = new Date().toISOString().slice(0, 10)
    triggerCsvDownload(content, `aktivitaeten_${datumStr}.csv`)
    setTimeout(() => { downloadInProgress.current = false }, 500)
  }

  return (
    <main className="activity-feed-page">
      <SectionHeader onExportCsv={handleExportCsv} streamConnected={connected} />

      {is404 ? (
        <ActivityComingSoon />
      ) : error ? (
        <ActivityError onRetry={refetch} />
      ) : (
        <>
          <FilterChips filterCategory={filterCategory} onFilterCategoryChange={setFilterCategory} />

          <ActivityList
            items={filteredItems}
            loading={loading}
            hasMore={hasMore}
            activeFilter={filterCategory === 'all' ? null : filterCategory}
            dayLimit={filterCategory === 'all' ? dayLimit : null}
            useMetric={useMetric}
            onLoadMore={loadMore}
          />
        </>
      )}
    </main>
  )
}
